"use client";

import {
  Alert,
  Button,
  Combobox,
  Dialog,
  Stack,
  Text,
} from "@softmax/design-system";
import type { ComboboxGroup } from "@softmax/design-system";
import { useRef, type RefObject } from "react";

export type GameQuickNavGroup = Readonly<ComboboxGroup>;
export function GameQuickNavDialog({
  open,
  query,
  groups,
  loading,
  error,
  onOpenChange,
  onQueryChange,
  onSelect,
  onRetry,
  returnFocusRef,
}: {
  readonly open: boolean;
  readonly query: string;
  readonly groups: readonly GameQuickNavGroup[];
  readonly loading: boolean;
  readonly error: string | null;
  readonly onOpenChange: (open: boolean) => void;
  readonly onQueryChange: (query: string) => void;
  readonly onSelect: (href: string) => void;
  readonly onRetry: () => void;
  readonly returnFocusRef: RefObject<HTMLElement | null>;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <Dialog
      title="Go to game"
      description="Type a Coworld or league name. Use ↑ ↓ to choose a league and Enter to open."
      open={open}
      onOpenChange={onOpenChange}
      finalFocusRef={returnFocusRef}
      initialFocusRef={inputRef}
    >
      <Stack gap={8} width="auto">
        <Combobox
          ref={inputRef}
          aria-label="Search games"
          placeholder="Search games…"
          options={groups}
          inputValue={query}
          onInputValueChange={onQueryChange}
          onValueChange={onSelect}
          emptyMessage={loading ? "Loading games…" : "No games found"}
        />
        {loading && (
          <Text size="small" tone="muted">
            Loading games…
          </Text>
        )}
        {error && (
          <Alert tone="danger" announcement="polite">
            {error}{" "}
            <Button size="sm" onClick={onRetry}>
              Retry games
            </Button>
          </Alert>
        )}
      </Stack>
    </Dialog>
  );
}
